import React, { useState } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import './AddCompanyModal.css';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanyAdded: (company: any) => void;
}

interface CompanyData {
  name: string;
  email: string;
  domain: string;
  logo_url: string;
  address: string;
  phone: string;
  website: string;
  description: string;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onCompanyAdded
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState<{
    user_code: string;
    admin_code: string;
  } | null>(null);

  const [companyData, setCompanyData] = useState<CompanyData>({
    name: '',
    email: '',
    domain: '',
    logo_url: '',
    address: '',
    phone: '',
    website: '',
    description: ''
  });

  const handleInputChange = (field: keyof CompanyData, value: string) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateCodes = () => {
    // Generate unique codes
    const userCode = 'USER_' + Math.random().toString(36).substr(2, 8).toUpperCase();
    const adminCode = 'ADMIN_' + Math.random().toString(36).substr(2, 8).toUpperCase();
    
    setGeneratedCodes({
      user_code: userCode,
      admin_code: adminCode
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyData.name.trim() || !companyData.email.trim()) {
      setError(t('common.company_name_email_required'));
      return;
    }

    // V2: Backend automatically generates codes, no need to validate
    // if (!generatedCodes) {
    //   setError(t('common.please_generate_codes_first'));
    //   return;
    // }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.COMPANIES, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...companyData
          // V2: Backend automatically generates user_code, admin_code, procurement_code
          // user_code: generatedCodes.user_code,
          // admin_code: generatedCodes.admin_code
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(t('common.company_added_successfully'));
        onCompanyAdded(result.data);
        
        // V2: Set generated codes from response
        if (result.codes) {
          setGeneratedCodes(result.codes);
        }
        
        // Keep modal open to show codes
        setTimeout(() => {
          onClose();
          setCompanyData({
            name: '',
            email: '',
            domain: '',
            logo_url: '',
            address: '',
            phone: '',
            website: '',
            description: ''
          });
          setGeneratedCodes(null);
          setSuccess('');
        }, 3000);
      } else {
        setError(result.message || t('common.failed_to_add_company'));
      }
    } catch (error) {
      console.error('Error adding company:', error);
      setError(t('common.failed_to_add_company'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCompanyData({
      name: '',
      email: '',
      domain: '',
      logo_url: '',
      address: '',
      phone: '',
      website: '',
      description: ''
    });
    setGeneratedCodes(null);
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('common.new_company')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-section">
              <h3>{t('common.company_information')}</h3>
              
              <div className="form-group">
                <label>{t('common.company_name')} *</label>
                <input
                  type="text"
                  value={companyData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('common.enter_company_name')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('common.company_email')} *</label>
                <input
                  type="email"
                  value={companyData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={t('common.enter_company_email')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('common.domain')}</label>
                <input
                  type="text"
                  value={companyData.domain}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  placeholder={t('common.enter_domain')}
                />
              </div>

              <div className="form-group">
                <label>{t('common.website')}</label>
                <input
                  type="url"
                  value={companyData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder={t('common.enter_website')}
                />
              </div>

              <div className="form-group">
                <label>{t('common.phone')}</label>
                <input
                  type="tel"
                  value={companyData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('common.enter_phone')}
                />
              </div>

              <div className="form-group">
                <label>{t('common.address')}</label>
                <textarea
                  value={companyData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder={t('common.enter_address')}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>{t('common.description')}</label>
                <textarea
                  value={companyData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('common.enter_description')}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>{t('common.logo_url')}</label>
                <input
                  type="url"
                  value={companyData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder={t('common.enter_logo_url')}
                />
              </div>
            </div>

            <div className="form-section">
              <h3>{t('common.registration_codes')}</h3>
              <p className="help-text">
                {t('common.generate_codes_help')}
              </p>

              {!generatedCodes ? (
                <button
                  type="button"
                  className="generate-codes-btn"
                  onClick={generateCodes}
                >
                  {t('common.generate_codes')}
                </button>
              ) : (
                <div className="codes-display">
                  <div className="code-item">
                    <label>{t('common.user_registration_code')}:</label>
                    <div className="code-value">
                      <span>{generatedCodes.user_code}</span>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => navigator.clipboard.writeText(generatedCodes.user_code)}
                      >
                        {t('common.copy')}
                      </button>
                    </div>
                    <small>{t('common.share_with_users')}</small>
                  </div>

                  <div className="code-item">
                    <label>{t('common.admin_registration_code')}:</label>
                    <div className="code-value">
                      <span>{generatedCodes.admin_code}</span>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => navigator.clipboard.writeText(generatedCodes.admin_code)}
                      >
                        {t('common.copy')}
                      </button>
                    </div>
                    <small>{t('common.share_with_admins')}</small>
                  </div>

                  <div className="warning">
                    <strong>⚠️ Important:</strong> {t('common.codes_important')}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting || !generatedCodes}
            >
              {isSubmitting ? t('common.adding_company') : t('common.add_company')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompanyModal; 